import { createToast } from '@/utils'
import type { FC } from 'react'
import { useCallback, useEffect } from 'react'
import useTranslate from '@/hooks/use-translate'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Popover from '@mui/material/Popover'
import type { PopoverProps } from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import Brightness6 from '@mui/icons-material/Brightness6'

import { RouterLink } from '@/components/RouterLink'
import { useAuth } from '@/hooks/use-auth'
import { useRouter } from '@/hooks/use-router'
import { paths } from '@/paths'
import { Issuer } from '@/types/auth'
import { PartialUser } from '@/api'
import { FeatureNames } from '@/lib/featureFlags'
import Logout from '@mui/icons-material/Logout'
import ManageAccounts from '@mui/icons-material/ManageAccounts'
import TuneOutlined from '@mui/icons-material/TuneOutlined'
import * as Sentry from '@sentry/browser'

import { AccountLanguageHoverRow } from './account-language-hover-row'
import { accountPopoverMenuRowSx } from './account-menu-row-sx'
import {
  ThemePaletteVisualTrack,
  useThemePaletteCycle,
} from './account-theme-palette-toggle'
import { densityPx } from '@/theme/base/density'
import { useFeatureFlags } from '@/hooks/use-feature-flags'

interface AccountPopoverProps {
  user: PartialUser
  anchorEl: null | Element
  onClose?: () => void
  open?: boolean
  anchorOrigin?: PopoverProps['anchorOrigin']
  transformOrigin?: PopoverProps['transformOrigin']
  PaperProps?: PopoverProps['PaperProps']
  logoutAsButton?: boolean
  hideBackdrop?: boolean
  BackdropProps?: PopoverProps['BackdropProps']
}

export const AccountPopover: FC<AccountPopoverProps> = (props) => {
  const {
    anchorEl,
    onClose,
    open,
    user: _userProp,
    anchorOrigin = { vertical: 'bottom', horizontal: 'center' },
    transformOrigin = { vertical: 'top', horizontal: 'center' },
    PaperProps: paperPropsProp,
    logoutAsButton: _logoutAsButton = false,
    hideBackdrop = true,
    BackdropProps,
    ...other
  } = props
  const router = useRouter()
  const auth = useAuth()
  const { isFeatureEnabled } = useFeatureFlags()

  useEffect(() => {
    localStorage.setItem('user', auth?.user ? JSON.stringify(auth?.user) : '')
    if (auth?.user?.email) {
      Sentry.setUser({
        email: auth.user.email,
      })
    }
  }, [auth.user])

  const handleLogout = useCallback(async (): Promise<void> => {
    try {
      onClose?.()

      switch (auth.issuer) {
        case Issuer.JWT: {
          await auth.signOut()
          break
        }

        default: {
          console.warn('Using an unknown Auth Issuer, did not log out')
        }
      }

      router.push(paths.auth.jwt.login)
    } catch (err) {
      console.error(err)
      createToast('error', 'Something went wrong!')
    }
  }, [auth, router, onClose])

  const { t } = useTranslate('nav')
  const { t: tCommon } = useTranslate('common')
  const { themeAriaLabel, cycleTheme } = useThemePaletteCycle()
  const canSeeSettings = isFeatureEnabled(FeatureNames.newSettings)

  const fullName = [auth.user?.profile?.firstName, auth.user?.profile?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim()

  const paperSx = {
    width: densityPx(200),
    ...(typeof paperPropsProp?.sx === 'object' &&
    paperPropsProp.sx !== null &&
    !Array.isArray(paperPropsProp.sx)
      ? paperPropsProp.sx
      : {}),
  }

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      disableScrollLock
      hideBackdrop={hideBackdrop}
      BackdropProps={BackdropProps}
      onClose={onClose}
      open={!!open}
      PaperProps={{
        ...paperPropsProp,
        sx: paperSx,
      }}
      {...other}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {fullName || '—'}
        </Typography>
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ mt: 0.5, wordBreak: 'break-word' }}
        >
          {auth.user?.email}
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ p: 1 }}>
        {canSeeSettings && (
          <ListItemButton
            component={RouterLink}
            data-testid="account-popover-profile"
            href={paths.dashboard.settings.index}
            onClick={onClose}
            sx={accountPopoverMenuRowSx}
          >
            <ListItemIcon>
              <ManageAccounts />
            </ListItemIcon>
            <ListItemText
              sx={{ my: 0 }}
              primary={
                <Typography variant="body2">{tCommon('profil')}</Typography>
              }
            />
          </ListItemButton>
        )}
        {!canSeeSettings && (
          <ListItemButton
            component={RouterLink}
            data-testid="account-popover-legacy-reglages"
            href={paths.dashboard.legacyAccountSettings}
            onClick={onClose}
            sx={accountPopoverMenuRowSx}
          >
            <ListItemIcon>
              <TuneOutlined />
            </ListItemIcon>
            <ListItemText
              sx={{ my: 0 }}
              primary={<Typography variant="body2">{t('account')}</Typography>}
            />
          </ListItemButton>
        )}
        <Divider sx={{ my: 1 }} />
        <AccountLanguageHoverRow
          label={tCommon('language')}
          parentOpen={!!open}
        />
        <Divider sx={{ my: 1 }} />
        <ListItemButton
          component="div"
          data-testid="account-popover-theme"
          disableRipple
          disableTouchRipple
          role="button"
          aria-label={themeAriaLabel}
          tabIndex={0}
          onClick={cycleTheme}
          onKeyDown={(event) => {
            if (event.key === ' ' || event.key === 'Enter') {
              event.preventDefault()
              cycleTheme()
            }
          }}
          sx={{
            ...accountPopoverMenuRowSx,
            gap: 1,
            width: '100%',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <ListItemIcon>
            <Brightness6 />
          </ListItemIcon>
          <ListItemText
            sx={{ flex: '1 1 auto', minWidth: 0, my: 0 }}
            primary={
              <Typography variant="body2">{tCommon('theme')}</Typography>
            }
          />
          <ThemePaletteVisualTrack />
        </ListItemButton>
        <Divider sx={{ my: 1 }} />
        <Button
          fullWidth
          color="inherit"
          startIcon={<Logout sx={{ color: 'red' }} />}
          variant="outlined"
          onClick={() => void handleLogout()}
          sx={{ justifyContent: 'center', borderColor: 'red' }}
        >
          <Typography variant="body2" color="red">
            {tCommon('logout')}
          </Typography>
        </Button>
      </Box>
    </Popover>
  )
}
